/**
 * Express Checkout — assign standard/expedited shipping + signature fee per bundle,
 * set cart attribute, then redirect to Shopify checkout.
 */
(function () {
  "use strict";

  const ATTR_KEY = "Express-checkout";
  const SESSION_RETURN_REFRESH_KEY = "chukar_express_checkout_return_refresh";
  /** TEMP DEBUG: set true to log cart state instead of redirecting to checkout. */
  const EXPRESS_CHECKOUT_DEBUG_DISABLE_REDIRECT = false;
  /** Bootstrap only — tierConfig() hydrates from window.expressCheckoutConfig.shippingVariants (Liquid). */
  const DEFAULT_TIER_CONFIG = {
    1: {
      standard: { variantId: "44113130487878", price: 12.95 },
      twoDay: { variantId: "44113130520646", price: 18.95 },
    },
    2: {
      standard: { variantId: "44113130487878", price: 12.95 },
      twoDay: { variantId: "44509266509894", price: 21.95 },
    },
    3: {
      standard: { variantId: "44113130487878", price: 12.95 },
      twoDay: { variantId: "44509266575430", price: 24.95 },
    },
  };

  const productTagCache = new Map();
  let inProgress = false;

  const US_STATE_NAME_TO_CODE = {
    ALABAMA: "AL", ALASKA: "AK", ARIZONA: "AZ", ARKANSAS: "AR", CALIFORNIA: "CA",
    COLORADO: "CO", CONNECTICUT: "CT", DELAWARE: "DE", "DISTRICT OF COLUMBIA": "DC",
    FLORIDA: "FL", GEORGIA: "GA", HAWAII: "HI", IDAHO: "ID", ILLINOIS: "IL",
    INDIANA: "IN", IOWA: "IA", KANSAS: "KS", KENTUCKY: "KY", LOUISIANA: "LA",
    MAINE: "ME", MARYLAND: "MD", MASSACHUSETTS: "MA", MICHIGAN: "MI", MINNESOTA: "MN",
    MISSISSIPPI: "MS", MISSOURI: "MO", MONTANA: "MT", NEBRASKA: "NE", NEVADA: "NV",
    "NEW HAMPSHIRE": "NH", "NEW JERSEY": "NJ", "NEW MEXICO": "NM", "NEW YORK": "NY",
    "NORTH CAROLINA": "NC", "NORTH DAKOTA": "ND", OHIO: "OH", OKLAHOMA: "OK", OREGON: "OR",
    PENNSYLVANIA: "PA", "RHODE ISLAND": "RI", "SOUTH CAROLINA": "SC", "SOUTH DAKOTA": "SD",
    TENNESSEE: "TN", TEXAS: "TX", UTAH: "UT", VERMONT: "VT", VIRGINIA: "VA",
    WASHINGTON: "WA", "WEST VIRGINIA": "WV", WISCONSIN: "WI", WYOMING: "WY",
    "PUERTO RICO": "PR",
  };

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function cartJsUrl() {
    const routes = window.routes || {};
    const base = rootUrl().replace(/\/?$/, "/");
    if (routes.cart_url) {
      const url = String(routes.cart_url);
      return url.endsWith(".js") ? url : url + ".js";
    }
    return base + "cart.js";
  }

  function cartUrl(path) {
    const base = rootUrl().replace(/\/?$/, "/");
    const routes = window.routes || {};
    if (path === "add") return routes.cart_add_url || base + "cart/add.js";
    if (path === "update") return routes.cart_update_url || base + "cart/update.js";
    return cartJsUrl();
  }

  function cartAjaxHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
  }

  /** Serialize cart API calls to avoid burst 403s from bot protection / rate limits. */
  let cartAjaxChain = Promise.resolve();

  function enqueueCartAjax(task) {
    const run = cartAjaxChain.then(() => task());
    cartAjaxChain = run.catch(function () {});
    return run;
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function isCartPayload(data) {
    return data && typeof data === "object" && Array.isArray(data.items);
  }

  async function parseCartResponse(res) {
    const text = await res.text();
    if (!text || text.trim().charAt(0) === "<") {
      throw new Error("Could not load cart");
    }
    try {
      return JSON.parse(text);
    } catch (_) {
      throw new Error("Could not load cart");
    }
  }

  function tierConfig() {
    return buildTierConfigFromShippingVariants();
  }

  function expressGetStandardPromoSettings() {
    return (typeof window !== "undefined" && window.standardPromoSettings) || {};
  }

  /** Cart-wide main-product total for Express Checkout promo ($125 threshold). */
  function expressIsStandardPromoQualified(merchandiseDollars) {
    const promo = expressGetStandardPromoSettings();
    if (!promo || !promo.enabled) return false;
    const threshold = Number(promo.threshold);
    if (isNaN(threshold) || threshold <= 0) return false;
    const total = Number(merchandiseDollars);
    if (isNaN(total) || total < threshold) return false;
    return !!promo.variantId;
  }

  function expressHydrateStandardPromoTierConfig(tierCfg) {
    if (!tierCfg) return;
    const promo = expressGetStandardPromoSettings();
    if (!promo || !promo.enabled || !promo.variantId) return;
    const price = Number(promo.price);
    const vid = String(promo.variantId);
    for (let t = 1; t <= 3; t++) {
      if (!tierCfg[t]) tierCfg[t] = {};
      tierCfg[t].standardPromo = {
        variantId: vid,
        price: !isNaN(price) && price >= 0 ? price : 0,
      };
    }
  }

  function expressGetEffectiveStandard(tierRow, merchandiseDollars) {
    if (!tierRow || !tierRow.standard) return null;
    if (
      expressIsStandardPromoQualified(merchandiseDollars) &&
      tierRow.standardPromo &&
      tierRow.standardPromo.variantId
    ) {
      return tierRow.standardPromo;
    }
    return tierRow.standard;
  }

  function expressCartMerchandiseTotalDollars(cartItems) {
    let total = 0;
    (cartItems || []).forEach(function (item, index) {
      if (!item || isGiftCardLine(item)) return;
      if (isBundleParentLine(item)) {
        const components = getBundleComponents(item, index, cartItems);
        total += merchTotalDollars(components || []);
        return;
      }
      if (isFreeGiftLine(item)) return;
      if (isMainProductProps(item.properties)) {
        const qty = item.quantity || 1;
        if (item.original_price != null && item.original_price > 0) {
          total += (item.original_price / 100) * qty;
        } else if (item.price != null) {
          total += (item.price / 100) * qty;
        }
      }
    });
    return Math.round(total * 100) / 100;
  }

  function expressCheckoutMapVariantToTierSlots(title) {
    const titleRaw = String(title || "");
    const titleLower = titleRaw.toLowerCase();
    if (titleLower.includes("custom")) return null;
    const t2 = /\bt2\b|tier\s*2/i.test(titleRaw);
    const t3 = /\bt3\b|tier\s*3/i.test(titleRaw);
    let slot = null;
    if (titleLower.includes("saturday")) slot = "saturday";
    else if (titleLower.includes("overnight")) slot = "overnight";
    else if (
      titleLower.includes("expedited") ||
      titleLower.includes("2-day") ||
      titleLower.includes("2 day")
    ) {
      slot = "twoDay";
    } else if (titleLower.includes("standard promo") || titleLower.includes("standard-promo")) {
      slot = "standardPromo";
    } else if (titleLower.includes("standard")) slot = "standard";
    if (!slot) return null;
    const tiers =
      slot === "saturday" || slot === "standard" || slot === "standardPromo"
        ? [1, 2, 3]
        : t3
          ? [3]
          : t2
            ? [2]
            : [1];
    return { slot: slot, tiers: tiers };
  }

  function buildTierConfigFromShippingVariants() {
    const base = JSON.parse(JSON.stringify(DEFAULT_TIER_CONFIG));
    const cfg = window.expressCheckoutConfig || {};
    const variants = Array.isArray(cfg.shippingVariants) ? cfg.shippingVariants : [];
    if (variants.length) {
      variants.forEach(function (v) {
        if (!v || v.id == null) return;
        const mapped = expressCheckoutMapVariantToTierSlots(v.title);
        if (!mapped) return;
        const cents = Number(v.price);
        if (!cents || isNaN(cents)) return;
        const dollars = cents / 100;
        mapped.tiers.forEach(function (tier) {
          const key = String(tier);
          if (!base[key]) base[key] = {};
          base[key][mapped.slot] = {
            variantId: String(v.id),
            price: dollars,
          };
        });
      });
    } else {
      const liquid = cfg.tierConfig;
      if (liquid && typeof liquid === "object") {
        Object.keys(liquid).forEach(function (tierKey) {
          if (!base[tierKey]) base[tierKey] = {};
          const row = liquid[tierKey];
          if (!row || typeof row !== "object") return;
          Object.keys(row).forEach(function (slot) {
            if (row[slot] && row[slot].variantId != null) {
              base[tierKey][slot] = {
                variantId: String(row[slot].variantId),
                price: Number(row[slot].price),
              };
            }
          });
        });
      }
    }
    expressHydrateStandardPromoTierConfig(base);
    return base;
  }

  function signatureFeeVariantId() {
    const id = window.expressCheckoutConfig && window.expressCheckoutConfig.signatureFeeVariantId;
    return id != null ? String(id) : null;
  }

  function signatureFeePriceDollars() {
    const cfg = window.expressCheckoutConfig || {};
    if (cfg.signatureFeePriceCents == null) return 0;
    const cents = Number(cfg.signatureFeePriceCents);
    return isNaN(cents) ? 0 : cents / 100;
  }

  function signatureFeeDisplayProperty() {
    return "$" + signatureFeePriceDollars().toFixed(2);
  }

  function parseMetaStateCsv(raw) {
    if (raw == null || raw === "") return [];
    let s = String(raw).trim();
    if (!s) return [];
    if (s.indexOf(":") !== -1) {
      s = s.substring(s.lastIndexOf(":") + 1).trim();
    }
    return s
      .split(",")
      .map(function (x) {
        return String(x).trim().toUpperCase();
      })
      .filter(Boolean);
  }

  function hotStatesList() {
    const cfg = window.expressCheckoutConfig || {};
    const hot = parseMetaStateCsv(cfg.hotStates);
    const veryHot = parseMetaStateCsv(cfg.veryHotStates);
    return hot.concat(veryHot).filter(function (v, i, a) {
      return v && a.indexOf(v) === i;
    });
  }

  function stateCodeFromProvinceName(province) {
    if (province == null) return "";
    const s = String(province).trim().toUpperCase();
    if (!s) return "";
    if (s.length === 2) return s;
    return US_STATE_NAME_TO_CODE[s] || "";
  }

  function parseBundleAddress(components) {
    const raw = findBundleAddress(components);
    if (raw == null || String(raw).trim() === "") return null;
    try {
      return typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch (_) {
      return null;
    }
  }

  function resolveAddressStateCode(address) {
    if (!address || typeof address !== "object") return "";
    if (address.province_code) {
      const code = String(address.province_code).trim().toUpperCase();
      if (code) return code;
    }
    if (address.province) {
      const province = String(address.province).trim().toUpperCase();
      if (province.length === 2) return province;
      const mapped = stateCodeFromProvinceName(province);
      if (mapped) return mapped;
    }
    if (address.zip && window.zipCodeLookup) {
      const z = String(address.zip).trim();
      const zd =
        window.zipCodeLookup[z] ||
        window.zipCodeLookup[z.replace(/^0+/, "")] ||
        window.zipCodeLookup[parseInt(z, 10)];
      if (zd && zd.state) return String(zd.state).trim().toUpperCase();
    }
    return "";
  }

  function isAddressKnown(address) {
    return resolveAddressStateCode(address) !== "";
  }

  function isHotStateAddress(address, stateList) {
    if (!address || !Array.isArray(stateList) || !stateList.length) return false;
    const code = resolveAddressStateCode(address);
    return code !== "" && stateList.includes(code);
  }

  function parseSummerMonths(raw) {
    if (raw == null || raw === "") return [4, 5, 6, 7, 8, 9, 10];
    return String(raw)
      .split(",")
      .map(function (m) {
        return parseInt(String(m).trim(), 10);
      })
      .filter(function (m) {
        return m >= 1 && m <= 12;
      });
  }

  function seasonalPerishableSlot() {
    const cfg =
      (window.expressCheckoutConfig && window.expressCheckoutConfig.perishableUnknownAddress) ||
      {};
    const summerMonths = parseSummerMonths(cfg.summerMonths);
    const month = new Date().getMonth() + 1;
    const isSummer = summerMonths.indexOf(month) >= 0;
    const slot = isSummer ? cfg.summerSlot : cfg.winterSlot;
    return slot === "twoDay" ? "twoDay" : "standard";
  }

  function resolvePerishableShippingSlot(components) {
    const address = parseBundleAddress(components);
    const hotStates = hotStatesList();
    if (isAddressKnown(address)) {
      return isHotStateAddress(address, hotStates) ? "twoDay" : "standard";
    }
    return seasonalPerishableSlot();
  }

  function lineHasDeliveryDate(props) {
    const p = normalizeLineProperties(props);
    return !!(
      (p._delivery_date != null && String(p._delivery_date).trim() !== "") ||
      (p.delivery_date != null && String(p.delivery_date).trim() !== "") ||
      (p["Delivery date"] != null && String(p["Delivery date"]).trim() !== "")
    );
  }

  function stripDeliveryDateProps(props) {
    const p = normalizeLineProperties(props);
    delete p._delivery_date;
    delete p.delivery_date;
    delete p["Delivery date"];
    return p;
  }

  function gidFromProps(props) {
    if (!props) return "";
    if (Array.isArray(props)) {
      const pair = props.find((p) => Array.isArray(p) && String(p[0]) === "_gid");
      return pair && pair[1] != null ? String(pair[1]).trim() : "";
    }
    return props._gid != null ? String(props._gid).trim() : "";
  }

  function propsEqualGid(props, gid) {
    return gidFromProps(props) === String(gid || "").trim();
  }

  /** cart.js / item_components may expose properties as an object or array of pairs. */
  function normalizeLineProperties(props) {
    if (!props) return {};
    if (Array.isArray(props)) {
      const out = {};
      props.forEach(function (pair) {
        if (Array.isArray(pair) && pair.length >= 2 && pair[0] != null) {
          out[String(pair[0])] = pair[1];
        }
      });
      return out;
    }
    if (typeof props === "object") return Object.assign({}, props);
    return {};
  }

  function getLineItemProp(props, key) {
    const p = normalizeLineProperties(props);
    return p[key];
  }

  function isMainProductProps(props) {
    return (
      getLineItemProp(props, "_product") === true ||
      getLineItemProp(props, "_product") === "true"
    );
  }

  function isGiftCardLine(item) {
    if (!item) return false;
    return (
      item.gift_card === true ||
      (item.product && item.product.gift_card === true) ||
      (item.properties && item.properties._gift_card === "true")
    );
  }

  function isFreeGiftProps(p) {
    if (!p) return false;
    return (
      getLineItemProp(p, "_free_gift") === true ||
      getLineItemProp(p, "_free_gift") === "true" ||
      getLineItemProp(p, "_free_gift_promo") === true ||
      getLineItemProp(p, "_free_gift_promo") === "true"
    );
  }

  function isConfiguredFreeGiftVariant(variantId) {
    try {
      const idStr = String(variantId != null ? variantId : "").trim();
      if (!idStr || !window.freeGiftSettings) return false;
      const ids = (window.freeGiftSettings.variantIds || []).concat(
        window.freeGiftSettings.promoGift && window.freeGiftSettings.promoGift.variantIds
          ? window.freeGiftSettings.promoGift.variantIds
          : []
      );
      return ids.map(String).includes(idStr);
    } catch (_) {
      return false;
    }
  }

  function isFreeGiftLine(line) {
    if (!line) return false;
    if (isFreeGiftProps(line.properties)) return true;
    const vid = line.variant_id != null ? line.variant_id : line.id;
    return isConfiguredFreeGiftVariant(vid);
  }

  function lineTitle(line) {
    return String(
      line.title || line.product_title || line.variant_title || ""
    ).toLowerCase();
  }

  function configuredShippingVariantIds() {
    const ids = new Set();
    try {
      const cfg = window.expressCheckoutConfig || {};
      (Array.isArray(cfg.shippingVariants) ? cfg.shippingVariants : []).forEach(
        function (v) {
          if (v && v.id != null) ids.add(String(v.id));
        }
      );
      const tiers = cfg.tierConfig;
      if (tiers && typeof tiers === "object") {
        Object.keys(tiers).forEach(function (tierKey) {
          const row = tiers[tierKey];
          if (!row) return;
          Object.keys(row).forEach(function (slot) {
            if (row[slot] && row[slot].variantId != null) {
              ids.add(String(row[slot].variantId));
            }
          });
        });
      }
      try {
        const promo = expressGetStandardPromoSettings();
        if (promo && promo.enabled && promo.variantId) {
          ids.add(String(promo.variantId));
        }
        if (promo && promo.standardVariantId) {
          ids.add(String(promo.standardVariantId));
        }
      } catch (_) {}
    } catch (_) {}
    return ids;
  }

  let shippingVariantIdCache = null;

  function getConfiguredShippingVariantIds() {
    if (!shippingVariantIdCache) {
      shippingVariantIdCache = configuredShippingVariantIds();
    }
    return shippingVariantIdCache;
  }

  function lineTitleMatchesShippingProduct(title) {
    const lower = String(title || "").toLowerCase();
    if (lower.includes("gift information") || lower.includes("gift info")) {
      return true;
    }
    if (lower.includes("shipping information")) return true;
    return lower.includes("shipping") && !lower.includes("signature");
  }

  /**
   * Shipping product variant (expedited-shipping) even when mis-tagged as promo free gift
   * (_free_gift_promo + _product). Free-gift.js uses this product for tier-2 promo gifts.
   */
  function isShippingProductVariantLine(line) {
    if (!line) return false;
    const vid = line.variant_id != null ? line.variant_id : line.id;
    if (vid != null && getConfiguredShippingVariantIds().has(String(vid))) {
      return true;
    }
    const handle =
      handleFromComponent(line) || (line.handle ? String(line.handle) : "");
    const shippingHandle =
      window.expressCheckoutConfig &&
      window.expressCheckoutConfig.shippingProductHandle
        ? String(window.expressCheckoutConfig.shippingProductHandle).toLowerCase()
        : "expedited-shipping";
    if (handle && String(handle).toLowerCase() === shippingHandle) return true;
    return lineTitleMatchesShippingProduct(
      line.title || line.product_title || line.variant_title || ""
    );
  }

  /** Top-level line to remove when bundle already carries shipping for the same gid. */
  function isOrphanStandaloneShippingCandidate(line, index, cartItems) {
    if (!line || !isStandaloneCartLine(line, index, cartItems)) return false;
    if (isShippingLine(line)) return true;
    if (isFeeLine(line)) return false;
    return isShippingProductVariantLine(line);
  }

  function isShippingLine(line) {
    if (!line) return false;
    const p = normalizeLineProperties(line.properties);
    if (isFreeGiftProps(p)) return false;
    if (p._product === true || p._product === "true") return false;
    if (p._shipping === true || p._shipping === "true") return true;
    const t = lineTitle(line);
    return (
      t.includes("gift information") ||
      t.includes("gift info") ||
      t.includes("shipping information") ||
      (t.includes("shipping") && !t.includes("signature"))
    );
  }

  function isFeeLine(line) {
    if (!line) return false;
    const p = normalizeLineProperties(line.properties);
    if (p._fee === true || p._fee === "true") return true;
    const t = lineTitle(line);
    return t.includes("adult signature") || t.includes("signature fee");
  }

  function isRibbonLine(line) {
    if (!line) return false;
    const p = normalizeLineProperties(line.properties);
    return (
      p._ribbon === true ||
      p._ribbon === "true" ||
      lineTitle(line).includes("ribbon")
    );
  }

  function isMainProductLine(line) {
    if (!line) return false;
    if (isShippingLine(line) || isFeeLine(line) || isRibbonLine(line)) return false;
    if (isFreeGiftLine(line)) return true;
    const p = normalizeLineProperties(line.properties);
    return (
      p._product === true ||
      p._product === "true" ||
      (!isShippingLine(line) && !isFeeLine(line))
    );
  }

  function isShippingPayload(item) {
    if (!item || !item.properties) return false;
    const p = normalizeLineProperties(item.properties);
    if (p._shipping === true || p._shipping === "true") return true;
    if (p._product === true || p._product === "true") return false;
    if (p._fee === true || p._fee === "true") return false;
    if (p._ribbon === true || p._ribbon === "true") return false;
    return p._address != null && String(p._address).trim() !== "";
  }

  function cartLineToComponent(item) {
    return {
      key: item.key,
      variant_id: item.variant_id,
      id: item.variant_id,
      quantity: item.quantity || 1,
      properties: normalizeLineProperties(item.properties),
      title: item.title,
      product_title: item.product_title,
      handle:
        item.handle || (item.product && item.product.handle) || "",
      url: item.url,
      product: item.product,
      product_tags:
        item.product && Array.isArray(item.product.tags)
          ? item.product.tags
          : undefined,
      original_price: item.original_price,
      price: item.price,
      final_price: item.final_price,
      final_line_price: item.final_line_price,
    };
  }

  function mergeComponents(existing, incoming) {
    const out = Array.isArray(existing) ? existing.slice() : [];
    if (!Array.isArray(incoming)) return out;
    let hasShip = out.some((c) => c && isShippingLine(c));
    incoming.forEach((comp) => {
      if (!comp) return;
      if (isShippingLine(comp)) {
        if (!hasShip) {
          out.push(comp);
          hasShip = true;
        }
        return;
      }
      const vid = comp.variant_id != null ? comp.variant_id : comp.id;
      if (vid == null) {
        out.push(comp);
        return;
      }
      const vidStr = String(vid);
      const idx = out.findIndex((c) => {
        if (!c || isShippingLine(c)) return false;
        const cv = c.variant_id != null ? c.variant_id : c.id;
        return cv != null && String(cv) === vidStr;
      });
      if (idx >= 0) out[idx] = comp;
      else out.push(comp);
    });
    return out;
  }

  /** Refresh Liquid-backed bundle components (cart.js often omits item_components). */
  async function refreshCartItemComponents() {
    try {
      const res = await fetch(rootUrl() + "?sections=cart-components-data", {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      const html = data["cart-components-data"];
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, "text/html");
      const scriptContent = doc.querySelector("#cart-components-data")?.textContent;
      if (scriptContent) new Function(scriptContent)();
    } catch (_) {
      /* non-fatal */
    }
  }

  function normalizeSectionComponent(comp) {
    if (!comp) return null;
    return {
      key: comp.line_key || comp.key,
      variant_id: comp.variant_id,
      id: comp.variant_id != null ? comp.variant_id : comp.id,
      quantity: comp.quantity || 1,
      properties: normalizeLineProperties(comp.properties),
      title: comp.title,
      product_title: comp.product_title,
      handle: comp.handle,
      url: comp.url,
      product: comp.product,
      product_tags: comp.product_tags,
      price: comp.price,
      original_price: comp.original_price,
      final_price: comp.final_price,
      final_line_price: comp.final_line_price,
    };
  }

  function getComponentsFromCartItemComponents(item) {
    if (!item || !item.key) return null;
    const map = window.cartItemComponents;
    if (!map || typeof map !== "object") return null;
    const bundle = map[item.key];
    if (!bundle || !Array.isArray(bundle.components) || !bundle.components.length) {
      return null;
    }
    return bundle.components
      .map(normalizeSectionComponent)
      .filter(Boolean);
  }

  function isBundleParentLine(item) {
    return (
      item &&
      (item.has_components === true || item.has_components === "true")
    );
  }

  function componentsFromFlatCartLines(cartItems, gid) {
    const targetGid = String(gid || "").trim();
    if (!targetGid || !Array.isArray(cartItems)) return [];
    const comps = [];
    cartItems.forEach((item) => {
      if (!item || isGiftCardLine(item) || isBundleParentLine(item)) return;
      if (gidFromProps(item.properties) !== targetGid) return;
      comps.push(cartLineToComponent(item));
    });
    return comps;
  }

  function getBundleComponents(item, index, cartItems) {
    if (!item) return null;

    if (Array.isArray(item.item_components) && item.item_components.length) {
      return item.item_components;
    }

    const fromSection = getComponentsFromCartItemComponents(item);
    if (fromSection && fromSection.length) return fromSection;

    if (typeof cartWithComp !== "undefined" && Array.isArray(cartWithComp) && item.key) {
      for (let i = 0; i < cartWithComp.length; i++) {
        const row = cartWithComp[i];
        if (
          row &&
          row.cart &&
          row.cart.key === item.key &&
          row.components &&
          row.components.length
        ) {
          return row.components;
        }
      }
    }
    if (
      typeof cartWithComp !== "undefined" &&
      Array.isArray(cartWithComp) &&
      index != null &&
      cartWithComp[index] &&
      cartWithComp[index].components &&
      cartWithComp[index].components.length
    ) {
      return cartWithComp[index].components;
    }

    const gid = gidFromProps(item.properties);
    if (gid && Array.isArray(cartItems)) {
      const flat = componentsFromFlatCartLines(cartItems, gid);
      if (flat.length) return flat;
    }

    return null;
  }

  function entryHasMainProduct(components) {
    return (components || []).some(
      (c) =>
        c &&
        isMainProductLine(c) &&
        !isShippingLine(c) &&
        !isFeeLine(c) &&
        !isRibbonLine(c)
    );
  }

  /** Add cart line keys / missing flat lines only — never duplicate component rows. */
  function enrichBundleEntryFromCart(entry, cartItems) {
    const gid = String(entry.gid || "").trim();
    if (!gid || !Array.isArray(cartItems)) return;

    const knownLineKeys = new Set();
    const knownProductVariants = new Set();
    (entry.components || []).forEach((c) => {
      if (!c) return;
      if (c.key) knownLineKeys.add(String(c.key));
      if (!isMainProductProps(c.properties) && !isMainProductLine(c)) return;
      const vid = c.variant_id != null ? c.variant_id : c.id;
      if (vid != null) knownProductVariants.add(String(vid));
    });

    cartItems.forEach((item) => {
      if (!item || isGiftCardLine(item)) return;
      if (gidFromProps(item.properties) !== gid) return;

      const keyStr = item.key ? String(item.key) : "";

      if (isBundleParentLine(item)) {
        if (keyStr && entry.parentKeys.indexOf(keyStr) < 0) {
          entry.parentKeys.push(keyStr);
        }
        if (!entryHasMainProduct(entry.components)) {
          const fromSection = getComponentsFromCartItemComponents(item);
          if (fromSection && fromSection.length) {
            entry.components = mergeComponents(entry.components, fromSection);
          } else if (item.item_components && item.item_components.length) {
            entry.components = mergeComponents(entry.components, item.item_components);
          }
        }
        return;
      }

      if (keyStr && entry.flatKeys.indexOf(keyStr) < 0) {
        entry.flatKeys.push(keyStr);
      }

      const vid =
        item.variant_id != null ? String(item.variant_id) : "";
      if (
        keyStr &&
        !knownLineKeys.has(keyStr) &&
        (!vid || !knownProductVariants.has(vid))
      ) {
        entry.components = mergeComponents(entry.components, [
          cartLineToComponent(item),
        ]);
        knownLineKeys.add(keyStr);
        if (vid) knownProductVariants.add(vid);
      }
    });
  }

  function countMerchProductComponents(components) {
    let count = 0;
    (components || []).forEach((c) => {
      if (!c || isShippingLine(c) || isFeeLine(c) || isRibbonLine(c)) return;
      if (isFreeGiftLine(c)) return;
      if (isMainProductLine(c)) count += 1;
    });
    return count;
  }

  function countMerchProductsInCart(cartItems) {
    let count = 0;
    (cartItems || []).forEach((item) => {
      if (!item || isGiftCardLine(item)) return;
      if (isBundleParentLine(item)) {
        (item.item_components || []).forEach((c) => {
          if (!c) return;
          if (isMainProductProps(c.properties)) count += 1;
        });
        return;
      }
      if (isMainProductProps(item.properties)) count += 1;
    });
    return count;
  }

  function handleFromComponent(line) {
    if (!line) return "";
    if (line.handle) return String(line.handle).trim();
    if (line.product && line.product.handle) {
      return String(line.product.handle).trim();
    }
    const url = line.url;
    if (url) {
      const match = String(url).match(/\/products\/([^/?#]+)/i);
      if (match && match[1]) return decodeURIComponent(match[1]);
    }
    return "";
  }

  function productTagsFromComponent(line) {
    if (!line) return null;
    if (line.product && Array.isArray(line.product.tags)) {
      return line.product.tags;
    }
    if (Array.isArray(line.product_tags)) return line.product_tags;
    return null;
  }

  /** Match multiship: has-perishable tag or any tag containing "perishable". */
  function tagsIndicatePerishable(tags) {
    if (!Array.isArray(tags) || !tags.length) return false;
    return tags.some((t) => {
      const lower = String(t || "").toLowerCase();
      return lower === "has-perishable" || lower.includes("perishable");
    });
  }

  function tagsIndicateWine(tags) {
    if (!Array.isArray(tags) || !tags.length) return false;
    return tags.some((t) => {
      const lower = String(t || "").toLowerCase();
      return lower === "chukar-wine" || lower === "not-our-wine";
    });
  }

  async function fetchProductTags(handle) {
    const h = String(handle || "").trim();
    if (!h) return [];
    if (productTagCache.has(h)) return productTagCache.get(h);
    try {
      const res = await fetch(rootUrl() + "products/" + encodeURIComponent(h) + ".js", {
        credentials: "same-origin",
      });
      if (!res.ok) {
        productTagCache.set(h, []);
        return [];
      }
      const data = await res.json();
      const tags = Array.isArray(data.tags)
        ? data.tags
        : typeof data.tags === "string"
          ? data.tags.split(",").map((t) => t.trim())
          : [];
      productTagCache.set(h, tags);
      return tags;
    } catch (_) {
      productTagCache.set(h, []);
      return [];
    }
  }

  function buildVariantProductIndex(cartItems) {
    const byVariant = new Map();
    function register(line) {
      if (!line) return;
      const vid =
        line.variant_id != null ? String(line.variant_id) : line.id != null ? String(line.id) : "";
      if (!vid) return;
      const prev = byVariant.get(vid) || {};
      const handle = handleFromComponent(line) || prev.handle || "";
      const tags = productTagsFromComponent(line) || prev.tags;
      byVariant.set(vid, {
        handle: handle || prev.handle,
        tags: tags || prev.tags,
        product: line.product || prev.product,
        url: line.url || prev.url,
      });
    }
    (cartItems || []).forEach((item) => {
      register(item);
      (item.item_components || []).forEach(register);
    });
    return byVariant;
  }

  function enrichComponentProductMeta(comp, variantIndex) {
    if (!comp || !variantIndex) return comp;
    const vid =
      comp.variant_id != null ? String(comp.variant_id) : comp.id != null ? String(comp.id) : "";
    if (!vid) return comp;
    const meta = variantIndex.get(vid);
    if (!meta) return comp;
    return Object.assign({}, comp, {
      handle: handleFromComponent(comp) || meta.handle || comp.handle,
      url: comp.url || meta.url,
      product: comp.product || meta.product,
      product_tags: productTagsFromComponent(comp) || meta.tags,
    });
  }

  function enrichComponentsWithCartProductMeta(components, cartItems) {
    const index = buildVariantProductIndex(cartItems);
    return (components || []).map((c) => enrichComponentProductMeta(c, index));
  }

  /** Prefer live cart.js unit prices (reflects discount codes) over stale component snapshots. */
  function resolveExpressMerchandiseUnitCents(comp) {
    if (!comp) return 0;
    if (comp.final_price != null && comp.final_price >= 0) return comp.final_price;
    if (comp.price != null && comp.price >= 0) return comp.price;
    if (comp.discounted_price != null && comp.discounted_price >= 0) {
      return comp.discounted_price;
    }
    if (comp.original_price != null && comp.original_price > 0) return comp.original_price;
    return 0;
  }

  function expressMerchandiseUnitDollars(comp) {
    const unitCents = resolveExpressMerchandiseUnitCents(comp);
    if (!unitCents) return 0;
    const dollars = unitCents >= 100 ? unitCents / 100 : unitCents;
    return parseFloat(dollars.toFixed(2));
  }

  /**
   * Mirror multi-ship stampMultishipMainProductFeeProps: stamp Gift + _main_fees from
   * discounted cart unit price, and pouch promo keys when original > final.
   */
  function stampExpressMainProductFeeProps(props, comp) {
    if (!props || !comp) return props;
    if (isFreeGiftLine(comp)) return props;
    const perUnit = expressMerchandiseUnitDollars(comp);
    if (!perUnit) return props;

    props._main_fees = String(perUnit);
    props.Gift = "$" + perUnit.toFixed(2);

    const origCents = comp.original_price;
    const finalCents =
      comp.final_price != null ? comp.final_price : comp.price;
    if (
      origCents != null &&
      finalCents != null &&
      origCents > finalCents
    ) {
      props._original_price = "$" + (origCents / 100).toFixed(2);
      props._discounted_price = "$" + (finalCents / 100).toFixed(2);
    }
    return props;
  }

  function enrichComponentCartLinePrices(comp, cartItems) {
    if (!comp || !Array.isArray(cartItems)) return comp;
    const compKey = comp.key ? String(comp.key) : "";
    const compGid = gidFromProps(comp.properties);
    const compVid =
      comp.variant_id != null
        ? String(comp.variant_id)
        : comp.id != null
          ? String(comp.id)
          : "";

    function applyPrices(source) {
      if (!source) return comp;
      return Object.assign({}, comp, {
        original_price:
          source.original_price != null ? source.original_price : comp.original_price,
        price: source.price != null ? source.price : comp.price,
        final_price:
          source.final_price != null ? source.final_price : comp.final_price,
        final_line_price:
          source.final_line_price != null
            ? source.final_line_price
            : comp.final_line_price,
        discounted_price:
          source.discounted_price != null
            ? source.discounted_price
            : comp.discounted_price,
      });
    }

    if (compKey) {
      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];
        if (!item || String(item.key) !== compKey) continue;
        return applyPrices(item);
      }
    }

    if (compGid && compVid) {
      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];
        if (!item || isGiftCardLine(item) || isBundleParentLine(item)) continue;
        if (gidFromProps(item.properties) !== compGid) continue;
        if (String(item.variant_id) !== compVid) continue;
        return applyPrices(item);
      }
      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];
        if (!item || !isBundleParentLine(item) || !item.item_components) continue;
        for (let j = 0; j < item.item_components.length; j++) {
          const ic = item.item_components[j];
          if (!ic) continue;
          if (gidFromProps(ic.properties) !== compGid) continue;
          if (String(ic.variant_id) !== compVid) continue;
          return applyPrices(ic);
        }
      }
    }

    return comp;
  }

  function enrichComponentsWithCartLinePrices(components, cartItems) {
    return (components || []).map(function (c) {
      return enrichComponentCartLinePrices(c, cartItems);
    });
  }

  function bundleMainProductPropsNeedDiscountSync(components) {
    for (let i = 0; i < (components || []).length; i++) {
      const c = components[i];
      if (!c || !isMainProductLine(c) || isFreeGiftLine(c)) continue;
      if (!isMainProductProps(c.properties)) continue;

      const expected = expressMerchandiseUnitDollars(c);
      if (!expected) continue;

      const props = normalizeLineProperties(c.properties);
      const giftRaw = props.Gift != null ? props.Gift : props.gift;
      if (giftRaw != null && String(giftRaw).trim() !== "") {
        const giftDollars = parseFloat(
          String(giftRaw).replace(/[^0-9.-]/g, "")
        );
        if (!isNaN(giftDollars) && Math.abs(giftDollars - expected) > 0.02) {
          return true;
        }
      } else {
        return true;
      }

      const origCents = c.original_price;
      const finalCents =
        c.final_price != null ? c.final_price : c.price;
      if (
        origCents != null &&
        finalCents != null &&
        origCents > finalCents &&
        !props._discounted_price
      ) {
        return true;
      }
    }
    return false;
  }

  async function lineHasPerishableTag(line) {
    const p = normalizeLineProperties(line.properties);
    if (p._perishable_bundle === true || p._perishable_bundle === "true") {
      return true;
    }
    const directTags = productTagsFromComponent(line);
    if (tagsIndicatePerishable(directTags)) return true;
    const handle = handleFromComponent(line);
    if (handle) {
      const tags = await fetchProductTags(handle);
      if (tagsIndicatePerishable(tags)) return true;
    }
    return false;
  }

  async function lineIsWine(line) {
    const p = normalizeLineProperties(line.properties);
    if (p._wine === true || p._wine === "true") return true;
    const directTags = productTagsFromComponent(line);
    if (tagsIndicateWine(directTags)) return true;
    const handle = handleFromComponent(line);
    if (!handle) return false;
    const tags = await fetchProductTags(handle);
    return tagsIndicateWine(tags);
  }

  function merchTotalDollars(components) {
    let total = 0;
    components.forEach((c) => {
      if (!c || !isMainProductLine(c) || isFreeGiftLine(c)) return;
      if (!isMainProductProps(c.properties)) return;
      const qty = c.quantity || 1;
      if (c.original_price != null && c.original_price > 0) {
        total += (c.original_price / 100) * qty;
      } else if (c.price != null) {
        total += (c.price / 100) * qty;
      }
    });
    return Math.round(total * 100) / 100;
  }

  function tierFromMerchTotal(dollars) {
    if (dollars >= 250) return 3;
    if (dollars >= 75) return 2;
    return 1;
  }

  async function analyzeBundle(components, options) {
    let hasPerishable = false;
    let hasWine = false;
    let wineQty = 0;
    let hasMerch = false;

    for (let i = 0; i < components.length; i++) {
      const c = components[i];
      if (!c) continue;
      if (isShippingLine(c) || isFeeLine(c) || isRibbonLine(c)) continue;
      if (!isMainProductLine(c)) continue;
      hasMerch = true;
      if (await lineHasPerishableTag(c)) hasPerishable = true;
      if (await lineIsWine(c)) {
        hasWine = true;
        wineQty += c.quantity || 1;
      }
    }

    const merchTotal = merchTotalDollars(components);
    const tier = tierFromMerchTotal(merchTotal);
    const slot = hasPerishable ? resolvePerishableShippingSlot(components) : "standard";
    const tiers = tierConfig();
    const tierRow = tiers[String(tier)] || tiers["1"] || DEFAULT_TIER_CONFIG[1];
    let slotRow = tierRow[slot] || tierRow.standard;
    if (slot === "standard") {
      const promoMerchTotal =
        options && options.cartMerchTotal != null && !isNaN(Number(options.cartMerchTotal))
          ? Number(options.cartMerchTotal)
          : merchTotalDollars(components);
      slotRow = expressGetEffectiveStandard(tierRow, promoMerchTotal) || slotRow;
    }

    return {
      hasPerishable,
      hasWine,
      wineQty,
      hasMerch,
      tier,
      slot,
      variantId: slotRow.variantId,
      shippingPrice: slotRow.price,
    };
  }

  function findBundleAddress(components) {
    for (let i = 0; i < components.length; i++) {
      const p = normalizeLineProperties(components[i] && components[i].properties);
      if (p._address != null && String(p._address).trim() !== "") {
        return p._address;
      }
    }
    return "";
  }

  /** Prefer shipping-line address (multiship saves recipient on the shipping component). */
  function findJobShippingAddress(components) {
    const shippingLines = (components || []).filter(function (c) {
      return c && isShippingLine(c);
    });
    for (let i = 0; i < shippingLines.length; i++) {
      const p = normalizeLineProperties(shippingLines[i].properties);
      if (p._address != null && String(p._address).trim() !== "") {
        return p._address;
      }
    }
    return findBundleAddress(components);
  }

  /**
   * Line properties for cart/add — Cart Transform requires _gid and _product on main lines.
   */
  function buildCartAddProperties(comp, gid) {
    const gidStr = String(gid || "").trim();
    const props = stripDeliveryDateProps(comp && comp.properties);
    props._gid = gidStr;

    if (isRibbonLine(comp)) {
      if (props._ribbon !== true && props._ribbon !== "true") props._ribbon = "true";
      return props;
    }
    if (isFeeLine(comp)) {
      if (props._fee !== true && props._fee !== "true") props._fee = "true";
      const existingFees = props.Fees != null ? props.Fees : props.fees;
      const feeNum =
        existingFees != null
          ? parseFloat(String(existingFees).replace(/[^0-9.-]/g, ""))
          : NaN;
      if (
        existingFees == null ||
        String(existingFees).trim() === "" ||
        isNaN(feeNum) ||
        feeNum <= 0
      ) {
        props.Fees = signatureFeeDisplayProperty();
      }
      return props;
    }
    if (isFreeGiftLine(comp)) {
      props._gid = gidStr;
      return props;
    }
    if (isShippingLine(comp)) {
      props._gid = gidStr;
      return props;
    }

    props._product = "true";
    stampExpressMainProductFeeProps(props, comp);
    return props;
  }

  function buildBundleItems(gid, components, analysis, options) {
    const items = [];
    const address =
      (options && options.anchorAddress) || findBundleAddress(components);
    let hasFee = false;

    components.forEach((comp) => {
      if (!comp) return;
      if (isShippingLine(comp)) return;
      if (isFeeLine(comp)) {
        hasFee = true;
        return;
      }
      const variantId = comp.variant_id != null ? comp.variant_id : comp.id;
      if (variantId == null) return;
      const vid =
        typeof variantId === "string"
          ? parseInt(variantId, 10) || variantId
          : variantId;
      let qty = comp.quantity || 1;
      if (isFreeGiftLine(comp)) qty = 1;
      items.push({
        id: vid,
        quantity: qty,
        properties: buildCartAddProperties(comp, gid),
      });
    });

    const shippingProps = {
      _gid: gid,
      _shipping: "true",
      _custom: "false",
      Shipping: "$" + Number(analysis.shippingPrice).toFixed(2),
    };
    if (address) shippingProps._address = address;
    if (analysis.hasPerishable) shippingProps._perishable_bundle = "true";

    items.push({
      id: parseInt(String(analysis.variantId), 10) || analysis.variantId,
      quantity: 1,
      properties: shippingProps,
    });

    if (analysis.hasWine) {
      const feeId = signatureFeeVariantId();
      const existingFee = components.find((c) => c && isFeeLine(c));
      if (hasFee && existingFee) {
        const fvid =
          existingFee.variant_id != null ? existingFee.variant_id : existingFee.id;
        items.push({
          id: parseInt(String(fvid), 10) || fvid,
          quantity: Math.max(1, analysis.wineQty || existingFee.quantity || 1),
          properties: buildCartAddProperties(existingFee, gid),
        });
      } else if (feeId) {
        items.push({
          id: parseInt(feeId, 10) || feeId,
          quantity: Math.max(1, analysis.wineQty || 1),
          properties: {
            _gid: gid,
            _fee: "true",
            Fees: signatureFeeDisplayProperty(),
          },
        });
      }
    } else if (hasFee) {
      const feeId = signatureFeeVariantId();
      const existingFee = components.find((c) => c && isFeeLine(c));
      if (feeId && existingFee) {
        const fvid =
          existingFee.variant_id != null ? existingFee.variant_id : existingFee.id;
        items.push({
          id: parseInt(String(fvid), 10) || fvid,
          quantity: existingFee.quantity || 1,
          properties: buildCartAddProperties(existingFee, gid),
        });
      }
    }

    items.sort((a, b) => {
      const w = (it) => (isShippingPayload(it) ? 2 : it.properties && it.properties._fee ? 1 : 0);
      return w(a) - w(b);
    });

    return items;
  }

  function normalizeVariantId(value) {
    return String(value != null ? value : "").trim();
  }

  function parseShippingPriceFromProps(props) {
    if (!props) return null;
    const p = normalizeLineProperties(props);
    const raw = p.Shipping || p.Fees || p.shipping || p.fees;
    if (raw == null || raw === "") return null;
    const match = String(raw).match(/([\d.]+)/);
    return match ? parseFloat(match[1]) : null;
  }

  /** Cart Transform bundle parent exists for this recipient gid. */
  function cartHasBundleParentForGid(cartItems, gid) {
    const targetGid = String(gid || "").trim();
    if (!targetGid || !Array.isArray(cartItems)) return false;
    return cartItems.some(function (item, index) {
      if (!item || !isBundleParentLine(item)) return false;
      if (gidFromProps(item.properties) === targetGid) return true;
      const comps = getBundleComponents(item, index, cartItems);
      if (!comps || !comps.length) return false;
      return comps.some(function (c) {
        return c && gidFromProps(c.properties) === targetGid;
      });
    });
  }

  /**
   * True when the bundle already has the express-checkout shipping variant (and wine fee if needed).
   * Skips full cart rebuild for performance when nothing would change.
   */
  function bundleAlreadyConfigured(components, analysis, cartItems, gid) {
    if (!cartHasBundleParentForGid(cartItems, gid)) return false;

    const shippingLines = (components || []).filter((c) => c && isShippingLine(c));
    if (shippingLines.length !== 1) return false;

    const shipping = shippingLines[0];
    const shipVid = normalizeVariantId(
      shipping.variant_id != null ? shipping.variant_id : shipping.id
    );
    const expectedVid = normalizeVariantId(analysis.variantId);
    if (!expectedVid || shipVid !== expectedVid) return false;

    const expectedPrice = Number(analysis.shippingPrice);
    const actualPrice = parseShippingPriceFromProps(shipping.properties || {});
    if (
      !isNaN(expectedPrice) &&
      actualPrice != null &&
      Math.abs(actualPrice - expectedPrice) > 0.02
    ) {
      return false;
    }

    if (analysis.hasPerishable) {
      const sp = shipping.properties || {};
      if (sp._perishable_bundle !== true && sp._perishable_bundle !== "true") {
        return false;
      }
    }

    if (lineHasDeliveryDate(shipping.properties)) return false;
    const hasProductDeliveryDate = (components || []).some(function (c) {
      return c && !isShippingLine(c) && lineHasDeliveryDate(c.properties);
    });
    if (hasProductDeliveryDate) return false;

    if (analysis.hasWine) {
      const feeId = signatureFeeVariantId();
      if (!feeId) return true;
      const feeLines = (components || []).filter((c) => c && isFeeLine(c));
      if (feeLines.length !== 1) return false;
      const fee = feeLines[0];
      const feeVid = normalizeVariantId(fee.variant_id != null ? fee.variant_id : fee.id);
      if (feeVid !== normalizeVariantId(feeId)) return false;
      const expectedQty = Math.max(1, analysis.wineQty || 1);
      if ((fee.quantity || 1) !== expectedQty) return false;
    }

    return true;
  }

  function generateExpressGid() {
    return Math.random().toString().slice(2, 11);
  }

  function addressFingerprint(address) {
    if (address == null || String(address).trim() === "") return "";
    try {
      const obj =
        typeof address === "string" ? JSON.parse(address) : address;
      const parts = [
        obj.address1,
        obj.address2,
        obj.city,
        obj.province,
        obj.province_code,
        obj.zip,
      ]
        .filter((v) => v != null && String(v).trim() !== "")
        .map((v) => String(v).trim().toLowerCase());
      return parts.join("|");
    } catch (_) {
      return String(address).trim().toLowerCase();
    }
  }

  /**
   * True when a bundle was configured on the multiship shipping page (recipient, calendar,
   * delivery date). Used for diagnostics; express checkout still consolidates via merge.
   */
  function isMultishipShippingPageBundle(components) {
    if (!components || !components.length) return false;
    if (
      components.some(function (c) {
        return c && !isShippingLine(c) && !isFeeLine(c) && lineHasDeliveryDate(c.properties);
      })
    ) {
      return true;
    }
    const shippingLines = components.filter(function (c) {
      return c && isShippingLine(c);
    });
    if (!shippingLines.length) return false;
    return shippingLines.some(function (s) {
      const p = normalizeLineProperties(s.properties);
      if (lineHasDeliveryDate(p)) return true;
      if (p._custom === true || p._custom === "true") return true;
      const addr = p._address;
      if (addr == null || String(addr).trim() === "") return false;
      try {
        const obj = typeof addr === "string" ? JSON.parse(addr) : addr;
        return !!(obj && (obj.address1 || obj.zip || obj.city));
      } catch (_) {
        return String(addr).trim().length > 2;
      }
    });
  }

  function allJobsAreMultishipShippingPageBundles(jobs) {
    return (
      Array.isArray(jobs) &&
      jobs.length > 0 &&
      jobs.every(function (job) {
        return isMultishipShippingPageBundle(job.components);
      })
    );
  }

  /** True when the multiship shipping-page UI is active (preserve multi-recipient bundles). */
  function isMultishipShippingPageContext() {
    try {
      return !!document.querySelector("shipping-page");
    } catch (_) {
      return false;
    }
  }

  /**
   * Express checkout: merge separate cart bundles into one shipment.
   * Always merge when multiple jobs exist — including from the shipping-page cart drawer.
   * (Standard multiship checkout uses a different control and keeps separate recipients.)
   */
  function shouldMergeBundlesForExpress(jobs) {
    return !!(jobs && jobs.length > 1);
  }

  /**
   * Merge components from separate bundles (multi-product express checkout).
   * Sums quantity when the same variant appears in different bundles.
   */
  function mergeComponentsAcrossBundles(existing, incoming) {
    const out = Array.isArray(existing) ? existing.slice() : [];
    if (!Array.isArray(incoming)) return out;

    incoming.forEach((comp) => {
      if (!comp) return;
      if (isShippingLine(comp) || isFeeLine(comp)) return;

      const vid = comp.variant_id != null ? comp.variant_id : comp.id;
      if (vid == null) {
        out.push(comp);
        return;
      }
      const vidStr = String(vid);
      const idx = out.findIndex((c) => {
        if (!c || isShippingLine(c) || isFeeLine(c)) return false;
        if (isRibbonLine(c) || isRibbonLine(comp)) return false;
        if (isFreeGiftLine(c) !== isFreeGiftLine(comp)) return false;
        const cv = c.variant_id != null ? c.variant_id : c.id;
        return cv != null && String(cv) === vidStr;
      });
      if (idx >= 0) {
        const base = out[idx];
        out[idx] = Object.assign({}, base, {
          quantity: (base.quantity || 1) + (comp.quantity || 1),
        });
      } else {
        out.push(comp);
      }
    });
    return out;
  }

  function mergeBundleJobsForExpress(jobs, cartItems) {
    if (!shouldMergeBundlesForExpress(jobs)) return jobs;

    const sourceGids = jobs
      .map(function (entry) {
        return entry && entry.gid != null ? String(entry.gid).trim() : "";
      })
      .filter(Boolean);

    const merged = {
      gid: pickCanonicalExpressGid(sourceGids, null, cartItems),
      sourceGids: sourceGids,
      parentKeys: [],
      flatKeys: [],
      components: [],
    };

    jobs.forEach((entry) => {
      (entry.parentKeys || []).forEach((k) => merged.parentKeys.push(k));
      (entry.flatKeys || []).forEach((k) => merged.flatKeys.push(k));
      if (!merged.anchorAddress) {
        const addr = findJobShippingAddress(entry.components);
        if (addr) merged.anchorAddress = addr;
      }
      const productComponents = (entry.components || []).filter(
        (c) => c && !isShippingLine(c) && !isFeeLine(c)
      );
      merged.components = mergeComponentsAcrossBundles(
        merged.components,
        productComponents
      );
    });

    console.log(
      "[ExpressCheckout] Merging",
      jobs.length,
      "bundles into one:",
      merged.gid,
      merged.sourceGids.length > 1 ? "(from " + merged.sourceGids.length + " gids)" : ""
    );
    return [merged];
  }

  /** Re-scan live cart keys so rebuild removes every line for merged / stale gids. */
  function refreshBundleEntryKeysFromCart(entry, cartItems) {
    if (!entry || !Array.isArray(cartItems)) return;
    const gids = new Set();
    if (Array.isArray(entry.sourceGids)) {
      entry.sourceGids.forEach(function (g) {
        if (g) gids.add(String(g));
      });
    }
    if (entry.gid) gids.add(String(entry.gid));
    if (!gids.size) return;

    const parentKeys = [];
    const flatKeys = [];
    const seenParents = new Set();
    const seenFlat = new Set();

    cartItems.forEach(function (item, index) {
      if (!item || isGiftCardLine(item) || !item.key) return;
      const keyStr = String(item.key);
      let matches = gids.has(gidFromProps(item.properties));

      if (!matches && isBundleParentLine(item)) {
        const comps = getBundleComponents(item, index, cartItems);
        if (
          comps &&
          comps.some(function (c) {
            return c && gids.has(gidFromProps(c.properties));
          })
        ) {
          matches = true;
        }
      }

      if (!matches) return;

      if (isBundleParentLine(item)) {
        if (!seenParents.has(keyStr)) {
          seenParents.add(keyStr);
          parentKeys.push(keyStr);
        }
      } else if (!seenFlat.has(keyStr)) {
        seenFlat.add(keyStr);
        flatKeys.push(keyStr);
      }
    });

    entry.parentKeys = parentKeys;
    entry.flatKeys = flatKeys;
  }

  function collectBundles(cartItems) {
    const byGid = new Map();

    cartItems.forEach((item, index) => {
      if (!item || isGiftCardLine(item)) return;

      const comps = getBundleComponents(item, index, cartItems);
      if (comps && comps.length) {
        const gid =
          comps.map((c) => gidFromProps(c.properties)).find((g) => g) ||
          gidFromProps(item.properties);
        if (!gid) return;
        const entry = byGid.get(gid) || {
          gid,
          parentKeys: [],
          flatKeys: [],
          components: [],
        };
        if (item.key) {
          const keyStr = String(item.key);
          if (isBundleParentLine(item)) {
            if (entry.parentKeys.indexOf(keyStr) < 0) entry.parentKeys.push(keyStr);
          } else if (entry.flatKeys.indexOf(keyStr) < 0) {
            entry.flatKeys.push(keyStr);
          }
        }
        entry.components = mergeComponents(entry.components, comps);
        byGid.set(gid, entry);
        return;
      }

      const gid = gidFromProps(item.properties);
      if (!gid) return;
      if (isGiftCardLine(item)) return;

      const entry = byGid.get(gid) || {
        gid,
        parentKeys: [],
        flatKeys: [],
        components: [],
      };
      if (item.key) {
        const keyStr = String(item.key);
        if (entry.flatKeys.indexOf(keyStr) < 0) entry.flatKeys.push(keyStr);
      }
      entry.components = mergeComponents(entry.components, [cartLineToComponent(item)]);
      byGid.set(gid, entry);
    });

    byGid.forEach((entry) => enrichBundleEntryFromCart(entry, cartItems));
    return byGid;
  }

  function isStandaloneCartLine(item, index, cartItems) {
    if (!item || !item.key) return false;
    if (isBundleParentLine(item)) return false;
    if (item.has_components === true || item.has_components === "true") {
      return false;
    }
    const comps = getBundleComponents(item, index, cartItems);
    if (
      comps &&
      comps.length > 0 &&
      (item.has_components === true || item.has_components === "true")
    ) {
      return false;
    }
    return true;
  }

  /** Every _gid present on a Cart Transform bundle or component group. */
  function collectBundledGidsFromCartItems(cartItems) {
    const gids = new Set();
    if (!Array.isArray(cartItems)) return gids;
    cartItems.forEach(function (item, index) {
      if (!item) return;
      const comps = getBundleComponents(item, index, cartItems);
      if (comps && comps.length > 0) {
        comps.forEach(function (c) {
          const g = gidFromProps(c.properties);
          if (g) gids.add(g);
        });
      }
      if (isBundleParentLine(item)) {
        const g = gidFromProps(item.properties);
        if (g) gids.add(g);
      }
    });
    return gids;
  }

  function cartHasAnyBundleParent(cartItems) {
    if (!Array.isArray(cartItems)) return false;
    if (
      cartItems.some(function (item) {
        return item && isBundleParentLine(item);
      })
    ) {
      return true;
    }
    try {
      const map = window.cartItemComponents;
      if (!map || typeof map !== "object") return false;
      return cartItems.some(function (item) {
        return item && item.key && map[String(item.key)];
      });
    } catch (_) {
      return false;
    }
  }

  /** GIDs that already have shipping inside a bundle parent (not standalone). */
  function collectGidsWithInBundleShipping(cartItems) {
    const gids = new Set();
    if (!Array.isArray(cartItems)) return gids;

    cartItems.forEach(function (item, index) {
      if (!item || !isBundleParentLine(item)) return;
      const comps = getBundleComponents(item, index, cartItems);
      if (!comps || !comps.length) return;
      comps.forEach(function (c) {
        if (c && isShippingLine(c)) {
          const g = gidFromProps(c.properties);
          if (g) gids.add(g);
        }
      });
    });

    try {
      const map = window.cartItemComponents;
      if (map && typeof map === "object") {
        Object.keys(map).forEach(function (parentKey) {
          const bundle = map[parentKey];
          if (!bundle || !Array.isArray(bundle.components)) return;
          bundle.components.forEach(function (comp) {
            const normalized = normalizeSectionComponent(comp);
            if (normalized && isShippingLine(normalized)) {
              const g = gidFromProps(normalized.properties);
              if (g) gids.add(g);
            }
          });
        });
      }
    } catch (_) {
      /* non-fatal */
    }

    return gids;
  }

  function collectRemovalGidsForEntry(entry) {
    const gids = new Set();
    if (!entry) return gids;
    if (entry.gid) gids.add(String(entry.gid));
    if (Array.isArray(entry.sourceGids)) {
      entry.sourceGids.forEach(function (g) {
        if (g) gids.add(String(g));
      });
    }
    return gids;
  }

  function appendStandaloneShippingFeeRemovalsForGids(updates, cartItems, gids) {
    if (!updates || !Array.isArray(cartItems) || !gids || !gids.size) return;
    cartItems.forEach(function (item, index) {
      if (!item || !item.key) return;
      if (!isStandaloneCartLine(item, index, cartItems)) return;
      if (
        !isShippingLine(item) &&
        !isFeeLine(item) &&
        !isShippingProductVariantLine(item)
      ) {
        return;
      }
      const gid = gidFromProps(item.properties);
      if (gid && gids.has(String(gid))) {
        updates[String(item.key)] = 0;
      }
    });
  }

  function componentLineKeysInJobs(jobs) {
    const keys = new Set();
    (jobs || []).forEach(function (job) {
      (job.components || []).forEach(function (c) {
        if (!c || !c.key) return;
        if (!isMainProductLine(c) || isShippingLine(c) || isFeeLine(c)) return;
        keys.add(String(c.key));
      });
    });
    return keys;
  }

  /** Include merchandise lines missed by gid grouping (e.g. mixed standard + express paths). */
  function enrichMergedJobWithUncollectedMerchandise(merged, cartItems, preMergeJobs) {
    if (!merged || !Array.isArray(cartItems)) return merged;
    const accountedKeys = componentLineKeysInJobs(preMergeJobs || [merged]);

    cartItems.forEach(function (item, index) {
      if (!item || isGiftCardLine(item)) return;
      if (isShippingLine(item) || isFeeLine(item) || isRibbonLine(item)) return;

      function maybeAddComponent(comp, lineKey) {
        if (!comp || !isMainProductLine(comp)) return;
        const keyStr =
          comp.key != null
            ? String(comp.key)
            : lineKey != null
              ? String(lineKey)
              : "";
        if (keyStr && accountedKeys.has(keyStr)) return;

        merged.components = mergeComponentsAcrossBundles(merged.components, [comp]);
        if (keyStr) {
          accountedKeys.add(keyStr);
          if (merged.flatKeys.indexOf(keyStr) < 0) {
            merged.flatKeys.push(keyStr);
          }
        }
      }

      if (isBundleParentLine(item)) {
        const comps = getBundleComponents(item, index, cartItems) || [];
        comps.forEach(function (c) {
          maybeAddComponent(c, null);
        });
        return;
      }

      if (!isMainProductLine(item)) return;
      maybeAddComponent(cartLineToComponent(item), item.key);
    });

    return merged;
  }

  function buildOrphanShippingUpdates(items, options) {
    options = options || {};
    const replacedGids = options.replacedGids || new Set();
    const expressCheckout = options.expressCheckout === true;
    const removeAllStandalone =
      options.removeAllStandaloneWhenBundleHasShipping === true;

    const gidsWithInBundleShipping = collectGidsWithInBundleShipping(items);
    const hasAnyInBundleShipping = gidsWithInBundleShipping.size > 0;
    const hasBundleParent = cartHasAnyBundleParent(items);
    const updates = {};

    items.forEach(function (item, index) {
      if (!item || !item.key) return;
      if (!isOrphanStandaloneShippingCandidate(item, index, items)) return;

      const gid = gidFromProps(item.properties);
      let shouldRemove = false;

      if (gid && gidsWithInBundleShipping.has(gid)) {
        shouldRemove = true;
      }
      if (
        gid &&
        replacedGids.has(String(gid)) &&
        (hasAnyInBundleShipping || hasBundleParent)
      ) {
        shouldRemove = true;
      }
      if (removeAllStandalone && hasAnyInBundleShipping) {
        shouldRemove = true;
      }
      // Express checkout: shipping belongs inside the bundle, not as a top-level line.
      if (
        expressCheckout &&
        (hasBundleParent || hasAnyInBundleShipping) &&
        (isShippingLine(item) || isShippingProductVariantLine(item))
      ) {
        shouldRemove = true;
      }

      if (shouldRemove) {
        updates[String(item.key)] = 0;
      }
    });

    return updates;
  }

  async function ensureExpressOrphanShippingRemoved(options) {
    const maxPasses = 4;
    let workingCart = null;

    for (let pass = 1; pass <= maxPasses; pass++) {
      // Caller may have just refreshed the component map (nothing mutated since) — skip pass 1.
      if (!(pass === 1 && options && options.componentsFresh)) {
        await refreshCartItemComponents();
      }
      workingCart = await fetchCart();
      const updates = buildOrphanShippingUpdates(workingCart.items || [], options);
      const keys = Object.keys(updates);
      if (!keys.length) return workingCart;

      console.log(
        "[ExpressCheckout] Removing",
        keys.length,
        "orphan standalone shipping line(s) (pass",
        pass + "/" + maxPasses + ")"
      );
      const updated = await cartUpdate({ updates });
      if (isCartPayload(updated)) workingCart = updated;
      await delay(400);
    }

    return workingCart || fetchCart();
  }

  async function fetchCart() {
    return enqueueCartAjax(async function () {
      for (let tryNum = 0; tryNum <= 4; tryNum++) {
        const res = await fetch(cartJsUrl(), {
          method: "GET",
          credentials: "same-origin",
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
        if (res.ok) {
          const data = await parseCartResponse(res);
          if (isCartPayload(data)) return data;
          throw new Error("Could not load cart");
        }
        if (
          (res.status === 403 || res.status === 429) &&
          tryNum < 4
        ) {
          await delay(400 * (tryNum + 1));
          continue;
        }
        throw new Error("Could not load cart");
      }
      throw new Error("Could not load cart");
    });
  }

  async function cartUpdate(body) {
    return enqueueCartAjax(async function () {
      const res = await fetch(cartUrl("update"), {
        method: "POST",
        credentials: "same-origin",
        headers: cartAjaxHeaders(),
        body: JSON.stringify(body),
      });
      const data = await parseCartResponse(res).catch(function () {
        return {};
      });
      if (!res.ok) {
        throw new Error(
          data.description || data.message || "Cart update failed"
        );
      }
      return data;
    });
  }

  async function cartAddItems(items) {
    const shipping = items.filter((it) => isShippingPayload(it));
    const other = items.filter((it) => !isShippingPayload(it));
    let lastCart = null;

    if (other.length) {
      lastCart = await enqueueCartAjax(async function () {
        const res = await fetch(cartUrl("add"), {
          method: "POST",
          credentials: "same-origin",
          headers: cartAjaxHeaders(),
          body: JSON.stringify({ items: other }),
        });
        const data = await parseCartResponse(res).catch(function () {
          return {};
        });
        if (!res.ok) {
          throw new Error(
            data.description || data.message || "Could not add cart items"
          );
        }
        return data;
      });
      // Only pause between the two adds; rebuildBundle() already waits after the last one.
      if (shipping.length) await delay(350);
    }

    if (shipping.length) {
      lastCart = await enqueueCartAjax(async function () {
        const res = await fetch(cartUrl("add"), {
          method: "POST",
          credentials: "same-origin",
          headers: cartAjaxHeaders(),
          body: JSON.stringify({ items: shipping }),
        });
        const data = await parseCartResponse(res).catch(function () {
          return {};
        });
        if (!res.ok) {
          throw new Error(
            data.description || data.message || "Could not add shipping"
          );
        }
        return data;
      });
    }

    return lastCart;
  }

  async function rebuildBundle(entry, itemsToAdd, cartItems) {
    const updates = {};
    (entry.parentKeys || []).forEach((k) => {
      updates[k] = 0;
    });
    (entry.flatKeys || []).forEach((k) => {
      updates[k] = 0;
    });
    appendStandaloneShippingFeeRemovalsForGids(
      updates,
      cartItems || [],
      collectRemovalGidsForEntry(entry)
    );
    let lastCart = null;
    if (Object.keys(updates).length) {
      lastCart = await cartUpdate({ updates });
      await delay(400);
    }
    const addCart = await cartAddItems(itemsToAdd);
    if (isCartPayload(addCart)) lastCart = addCart;
    await delay(400);
    return lastCart;
  }

  function bundleComponentsHaveMixedGids(components) {
    const gids = new Set();
    (components || []).forEach(function (c) {
      if (!c) return;
      const g = gidFromProps(c.properties);
      if (g) gids.add(String(g));
    });
    return gids.size > 1;
  }

  function bundleComponentsAllUseGid(components, gid) {
    const target = String(gid || "").trim();
    if (!target || !(components || []).length) return false;
    for (let i = 0; i < components.length; i++) {
      const c = components[i];
      if (!c) continue;
      const g = gidFromProps(c.properties);
      if (!g || String(g) !== target) return false;
    }
    return true;
  }

  function visitCartLineGid(line, gids) {
    if (!line) return;
    const g = gidFromProps(line.properties);
    if (g) gids.add(String(g));
  }

  function collectDistinctGidsFromCartItems(cartItems) {
    const gids = new Set();
    if (!Array.isArray(cartItems)) return gids;
    cartItems.forEach(function (item, index) {
      if (!item || isGiftCardLine(item)) return;
      visitCartLineGid(item, gids);
      const comps = getBundleComponents(item, index, cartItems);
      if (comps) {
        comps.forEach(function (c) {
          visitCartLineGid(c, gids);
        });
      }
    });
    try {
      const map = window.cartItemComponents;
      if (map && typeof map === "object") {
        Object.keys(map).forEach(function (parentKey) {
          const bundle = map[parentKey];
          if (!bundle || !Array.isArray(bundle.components)) return;
          bundle.components.forEach(function (comp) {
            visitCartLineGid(normalizeSectionComponent(comp), gids);
          });
        });
      }
    } catch (_) {}
    return gids;
  }

  function cartGidHasRecipientAddress(cartItems, gid) {
    const target = String(gid || "").trim();
    if (!target || !Array.isArray(cartItems)) return false;
    let found = false;
    function check(line) {
      if (!line || found) return;
      if (gidFromProps(line.properties) !== target) return;
      const addr = getLineItemProp(line.properties, "_address");
      if (addr != null && String(addr).trim() !== "") found = true;
    }
    cartItems.forEach(function (item, index) {
      check(item);
      const comps = getBundleComponents(item, index, cartItems);
      if (comps) comps.forEach(check);
    });
    return found;
  }

  function pickCanonicalExpressGid(gids, entry, cartItems) {
    const list = (Array.isArray(gids) ? gids : [])
      .map(function (g) {
        return g != null ? String(g).trim() : "";
      })
      .filter(Boolean);
    if (!list.length) {
      return (entry && entry.gid) || generateExpressGid();
    }
    for (let i = 0; i < list.length; i++) {
      if (cartItems && cartGidHasRecipientAddress(cartItems, list[i])) {
        return list[i];
      }
    }
    if (entry && entry.gid && list.indexOf(String(entry.gid)) >= 0) {
      return String(entry.gid);
    }
    return list[0];
  }

  /** One canonical _gid for express checkout; superseded gids tracked for removal. */
  function ensureExpressCheckoutUnifiedGidOnJob(entry, cartItems) {
    if (!entry) return entry;
    const allGids = new Set(collectDistinctGidsFromCartItems(cartItems || []));
    if (entry.gid) allGids.add(String(entry.gid));
    if (Array.isArray(entry.sourceGids)) {
      entry.sourceGids.forEach(function (g) {
        if (g) allGids.add(String(g));
      });
    }
    const gidsList = Array.from(allGids).filter(Boolean);
    const canonical = pickCanonicalExpressGid(gidsList, entry, cartItems);
    const superseded = [];
    gidsList.forEach(function (g) {
      if (g !== canonical) superseded.push(g);
    });
    entry.gid = canonical;
    entry.sourceGids = superseded.length ? superseded : [];
    return entry;
  }

  function cartLinesMismatchTargetGid(cartItems, targetGid) {
    const target = String(targetGid || "").trim();
    if (!target || !Array.isArray(cartItems)) return false;
    let mismatch = false;
    function check(line) {
      if (!line || mismatch || isGiftCardLine(line)) return;
      const g = gidFromProps(line.properties);
      if (!g) return;
      if (String(g) !== target) mismatch = true;
    }
    cartItems.forEach(function (item, index) {
      if (!item || isGiftCardLine(item)) return;
      check(item);
      const comps = getBundleComponents(item, index, cartItems);
      if (comps) comps.forEach(check);
    });
    return mismatch;
  }

  function expressCheckoutRequiresGidRebuild(entry, cartItems) {
    if (!entry) return true;
    const target = String(entry.gid || "").trim();
    if (!target) return true;
    if (collectDistinctGidsFromCartItems(cartItems).size > 1) return true;
    if (bundleComponentsHaveMixedGids(entry.components)) return true;
    if (!bundleComponentsAllUseGid(entry.components, target)) return true;
    if (cartLinesMismatchTargetGid(cartItems, target)) return true;
    if (entry.sourceGids && entry.sourceGids.length > 0) return true;
    return false;
  }

  async function resolveBundleJobs(cart) {
    const bundles = collectBundles(cart.items || []);
    let jobs = [];

    bundles.forEach((entry) => {
      if (!entry.components || !entry.components.length) return;
      const hasProduct = entry.components.some(
        (c) => c && isMainProductLine(c) && !isShippingLine(c) && !isFeeLine(c)
      );
      if (!hasProduct) return;
      jobs.push(entry);
    });

    const preMergeJobs = jobs.slice();
    jobs = mergeBundleJobsForExpress(jobs, cart.items || []);

    jobs.forEach(function (job) {
      ensureExpressCheckoutUnifiedGidOnJob(job, cart.items || []);
    });

    if (jobs.length === 1 && jobs[0]) {
      enrichMergedJobWithUncollectedMerchandise(
        jobs[0],
        cart.items || [],
        preMergeJobs
      );
      refreshBundleEntryKeysFromCart(jobs[0], cart.items || []);
    }

    return jobs;
  }

  async function refreshCartDrawerSection() {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || typeof drawer.getSectionsToRender !== "function") return;
    try {
      const sectionIds = drawer
        .getSectionsToRender()
        .map((s) => s.id)
        .join(",");
      const res = await fetch(rootUrl() + "?sections=" + encodeURIComponent(sectionIds), {
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.sections) {
        drawer.renderContents({ sections: data.sections }, { openDrawer: false });
      }
    } catch (_) {
      /* non-fatal */
    }
  }

  async function processBundles(cart) {
    await refreshCartItemComponents();

    let jobs = await resolveBundleJobs(cart);

    let jobIndex = 0;
    let workingCart = cart;
    let anyRebuilt = false;
    const expressReplacedGids = new Set();
    let expressConsolidated = false;

    jobs.forEach(function (job) {
      if (
        job &&
        Array.isArray(job.sourceGids) &&
        job.sourceGids.length > 0
      ) {
        expressConsolidated = true;
        job.sourceGids.forEach(function (g) {
          if (g) expressReplacedGids.add(String(g));
        });
      }
    });

    for (let i = 0; i < jobs.length; i++) {
      let entry = jobs[i];
      if (!entry) continue;

      ensureExpressCheckoutUnifiedGidOnJob(entry, workingCart.items || []);

      const merchInJob = countMerchProductComponents(entry.components);
      const merchInCart = countMerchProductsInCart(workingCart.items || []);
      if (merchInCart > merchInJob) {
        console.warn(
          "[ExpressCheckout] Incomplete bundle snapshot; refreshing components",
          { merchInJob, merchInCart, gid: entry.gid }
        );
        await refreshCartItemComponents();
        workingCart = await fetchCart();
        const targetGid = entry.gid;
        jobs = await resolveBundleJobs(workingCart);
        entry =
          jobs.find((j) => j && j.gid === targetGid) ||
          (jobs.length === 1 ? jobs[0] : jobs[i]);
        if (!entry) continue;
      }

      entry.components = enrichComponentsWithCartProductMeta(
        entry.components,
        workingCart.items || []
      );
      entry.components = enrichComponentsWithCartLinePrices(
        entry.components,
        workingCart.items || []
      );
      const cartMerchTotalDollars = expressCartMerchandiseTotalDollars(workingCart.items || []);
      const analysis = await analyzeBundle(entry.components, {
        cartMerchTotal: cartMerchTotalDollars,
      });
      if (!analysis.hasMerch) continue;

      const requiresGidRebuild = expressCheckoutRequiresGidRebuild(
        entry,
        workingCart.items || []
      );
      const isMergedExpressBundle =
        Array.isArray(entry.sourceGids) && entry.sourceGids.length > 0;

      if (
        !requiresGidRebuild &&
        !isMergedExpressBundle &&
        bundleAlreadyConfigured(
          entry.components,
          analysis,
          workingCart.items || [],
          entry.gid
        ) &&
        !bundleMainProductPropsNeedDiscountSync(entry.components)
      ) {
        console.log(
          "[ExpressCheckout] Skipping bundle (shipping and _gid already correct):",
          entry.gid
        );
        continue;
      }

      if (requiresGidRebuild) {
        console.log(
          "[ExpressCheckout] Rebuilding bundle to unify _gid:",
          entry.gid,
          entry.sourceGids && entry.sourceGids.length
            ? "(superseding " + entry.sourceGids.join(", ") + ")"
            : ""
        );
      }

      jobIndex += 1;
      const activeButton = document.querySelector(
        "#express-checkout.is-loading, [data-express-checkout].is-loading"
      );
      if (activeButton && jobIndex > 1) {
        setButtonLoading(
          activeButton,
          true,
          "Updating shipment " + jobIndex + "…"
        );
      }

      refreshBundleEntryKeysFromCart(entry, workingCart.items || []);
      if (Array.isArray(entry.sourceGids) && entry.sourceGids.length > 0) {
        entry.sourceGids.forEach(function (g) {
          if (g) expressReplacedGids.add(String(g));
        });
      }
      const itemsToAdd = buildBundleItems(entry.gid, entry.components, analysis, {
        anchorAddress: entry.anchorAddress,
      });
      const rebuiltCart = await rebuildBundle(
        entry,
        itemsToAdd,
        workingCart.items || []
      );
      anyRebuilt = true;
      if (isCartPayload(rebuiltCart)) workingCart = rebuiltCart;
    }

    const activeButton = document.querySelector(
      "#express-checkout.is-loading, [data-express-checkout].is-loading"
    );
    if (activeButton) {
      setButtonLoading(activeButton, true, "Opening checkout…");
    }

    await refreshCartItemComponents();
    const orphanCart = await ensureExpressOrphanShippingRemoved({
      replacedGids: expressReplacedGids,
      removeAllStandaloneWhenBundleHasShipping:
        expressConsolidated || cartHasAnyBundleParent(workingCart.items || []),
      expressCheckout: true,
      componentsFresh: true,
    });
    if (isCartPayload(orphanCart)) workingCart = orphanCart;
    else if (anyRebuilt && !isCartPayload(workingCart)) {
      workingCart = await fetchCart();
    }

    if (anyRebuilt) {
      await refreshCartDrawerSection();
    }

    return workingCart;
  }

  async function setExpressCheckoutAttribute() {
    await cartUpdate({
      attributes: {
        [ATTR_KEY]: "true",
      },
    });
  }

  async function clearExpressCheckoutCartAttribute() {
    await cartUpdate({
      attributes: {
        [ATTR_KEY]: "false",
      },
    });
  }

  /** Returns true when the attribute was active and has been cleared to "false". */
  async function clearExpressCheckoutCartAttributeIfActive() {
    try {
      const cart = await fetchCart();
      const val = cart.attributes && cart.attributes[ATTR_KEY];
      if (val !== true && val !== "true") return false;
      await clearExpressCheckoutCartAttribute();
      return true;
    } catch (_) {
      return false;
    }
  }

  const LOADER_MESSAGE = "Preparing checkout…";

  function getDrawerLoader() {
    return document.getElementById("express-checkout-loader");
  }

  function setDrawerLoader(visible, message) {
    const loader = getDrawerLoader();
    if (!loader) return;
    const msgEl = loader.querySelector(".express-checkout-loader__message");
    if (msgEl && message) msgEl.textContent = message;
    if (visible) {
      loader.removeAttribute("hidden");
      loader.setAttribute("aria-hidden", "false");
    } else {
      loader.setAttribute("hidden", "");
      loader.setAttribute("aria-hidden", "true");
    }
  }

  function setButtonLoading(button, loading, message) {
    if (!button) return;
    const label = button.querySelector(".cart__express-checkout-label");
    const text = message || LOADER_MESSAGE;

    if (loading) {
      button.classList.add("is-loading");
      button.setAttribute("aria-busy", "true");
      button.setAttribute("aria-disabled", "true");
      if (!button.dataset.expressOriginalLabel) {
        button.dataset.expressOriginalLabel =
          (label && label.textContent.trim()) || button.textContent.trim();
      }
      if (label) label.textContent = text;
      setDrawerLoader(true, text);
      const drawer = button.closest("cart-drawer");
      if (drawer) drawer.classList.add("express-checkout-active");
    } else {
      button.classList.remove("is-loading");
      button.removeAttribute("aria-busy");
      button.removeAttribute("aria-disabled");
      if (label && button.dataset.expressOriginalLabel) {
        label.textContent = button.dataset.expressOriginalLabel;
      }
      setDrawerLoader(false);
      const drawer = button.closest("cart-drawer");
      if (drawer) drawer.classList.remove("express-checkout-active");
    }
  }

  /** Reset button + drawer loader (e.g. before redirect or when page is restored from bfcache). */
  function isFreeGiftCartBusy() {
    return !!(
      window.__freeGiftCartBusy ||
      window.__freeGiftMutationInProgress ||
      (window.freeGiftManager && window.freeGiftManager.processing)
    );
  }

  function resetExpressCheckoutUI() {
    inProgress = false;
    setDrawerLoader(false);
    document.querySelectorAll("cart-drawer").forEach((drawer) => {
      drawer.classList.remove("express-checkout-active");
    });
    if (isFreeGiftCartBusy()) {
      return;
    }
    document
      .querySelectorAll("#express-checkout, [data-express-checkout]")
      .forEach((button) => {
        button.classList.remove("is-loading");
        button.removeAttribute("aria-busy");
        button.removeAttribute("aria-disabled");
        const label = button.querySelector(".cart__express-checkout-label");
        if (label && button.dataset.expressOriginalLabel) {
          label.textContent = button.dataset.expressOriginalLabel;
        }
      });
    const msgEl = getDrawerLoader()?.querySelector(".express-checkout-loader__message");
    if (msgEl) msgEl.textContent = LOADER_MESSAGE;
  }

  function markReturnRefreshPending() {
    try {
      sessionStorage.setItem(SESSION_RETURN_REFRESH_KEY, "1");
    } catch (_) {}
  }

  function isReturningFromCheckoutPage() {
    try {
      const ref = document.referrer || "";
      return /\/checkout/i.test(ref);
    } catch (_) {
      return false;
    }
  }

  function shouldRefreshAfterExpressCheckout(event) {
    try {
      if (sessionStorage.getItem(SESSION_RETURN_REFRESH_KEY) === "1") {
        sessionStorage.removeItem(SESSION_RETURN_REFRESH_KEY);
        return true;
      }
    } catch (_) {}
    if (event && event.persisted && isReturningFromCheckoutPage()) return true;
    return !!(event && event.persisted);
  }

  function cloneForDebugLog(value) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return value;
    }
  }

  function summarizeCartLineForDebug(item, index, cartItems) {
    if (!item) return null;
    const props = normalizeLineProperties(item.properties);
    const comps = getBundleComponents(item, index, cartItems);
    const liquidEntry =
      window.cartItemComponents && item.key
        ? window.cartItemComponents[String(item.key)] || null
        : null;

    return {
      index: index,
      key: item.key,
      title: item.title,
      product_title: item.product_title,
      variant_id: item.variant_id,
      product_id: item.product_id,
      quantity: item.quantity,
      price: item.price,
      final_price: item.final_price,
      has_components: item.has_components,
      properties: props,
      gid: gidFromProps(props),
      recipientAddress: getLineItemProp(props, "_address"),
      deliveryDate: getLineItemProp(props, "_delivery_date"),
      flags: {
        isBundleParent: isBundleParentLine(item),
        isStandaloneLine: isStandaloneCartLine(item, index, cartItems),
        isShippingLine: isShippingLine(item),
        isShippingProductVariant: isShippingProductVariantLine(item),
        isOrphanShippingCandidate: isOrphanStandaloneShippingCandidate(
          item,
          index,
          cartItems
        ),
        isFeeLine: isFeeLine(item),
        isMainProduct: isMainProductLine(item),
        isMainProductProps: isMainProductProps(props),
      },
      item_components_from_cart_js: item.item_components || null,
      resolvedComponents: comps ? cloneForDebugLog(comps) : null,
      liquidComponentData: liquidEntry ? cloneForDebugLog(liquidEntry) : null,
    };
  }

  /** TEMP DEBUG: log cart state at the redirect point (uses cart-components-data.liquid). */
  async function logExpressCheckoutDebugBeforeRedirect() {
    await refreshCartItemComponents();
    const cart = await fetchCart();
    const items = cart.items || [];
    const componentMap = window.cartItemComponents || {};

    const gidsWithInBundleShipping = collectGidsWithInBundleShipping(items);
    const bundledGids = collectBundledGidsFromCartItems(items);
    const hasBundleParent = cartHasAnyBundleParent(items);
    const standaloneShippingLines = [];

    items.forEach(function (item, index) {
      if (!isOrphanStandaloneShippingCandidate(item, index, items)) return;
      standaloneShippingLines.push({
        key: item.key,
        title: item.title,
        gid: gidFromProps(item.properties),
        properties: normalizeLineProperties(item.properties),
        isShippingProductVariant: isShippingProductVariantLine(item),
        isShippingLine: isShippingLine(item),
      });
    });

    const orphanUpdates = buildOrphanShippingUpdates(items, {
      expressCheckout: true,
      removeAllStandaloneWhenBundleHasShipping:
        hasBundleParent || gidsWithInBundleShipping.size > 0,
      replacedGids: new Set(),
    });

    console.group(
      "[ExpressCheckout DEBUG] Pre-redirect cart snapshot (redirect disabled)"
    );
    console.log("Timestamp:", new Date().toISOString());
    console.log("Checkout URL (not navigating):", rootUrl().replace(/\/?$/, "") + "/checkout");
    console.log("Cart attributes:", cloneForDebugLog(cart.attributes || {}));
    console.log("Cart totals:", {
      item_count: cart.item_count,
      total_price: cart.total_price,
      items_subtotal_price: cart.items_subtotal_price,
    });

    console.group("1. All cart line items (cart.js)");
    items.forEach(function (item, index) {
      console.log("Line " + index + ":", summarizeCartLineForDebug(item, index, items));
    });
    console.groupEnd();

    console.group("2. window.cartItemComponents (sections/cart-components-data.liquid)");
    Object.keys(componentMap).forEach(function (parentKey) {
      const bundle = componentMap[parentKey];
      console.log("Parent key " + parentKey + ":", cloneForDebugLog(bundle));
      if (bundle && Array.isArray(bundle.components)) {
        bundle.components.forEach(function (comp, compIndex) {
          const compProps = normalizeLineProperties(comp && comp.properties);
          console.log("  Component " + compIndex + ":", {
            title: comp.title,
            variant_id: comp.variant_id,
            quantity: comp.quantity,
            line_key: comp.line_key || comp.key,
            properties: compProps,
            gid: gidFromProps(compProps),
            isShipping: isShippingLine(comp),
            isMainProduct: isMainProductProps(compProps),
            recipientAddress: getLineItemProp(compProps, "_address"),
            deliveryDate: getLineItemProp(compProps, "_delivery_date"),
          });
        });
      }
    });
    if (!Object.keys(componentMap).length) {
      console.warn("window.cartItemComponents is empty — cart-components-data may not have loaded.");
    }
    console.groupEnd();

    console.group("3. Bundle / shipping / recipient summary");
    console.log("hasBundleParent:", hasBundleParent);
    console.log("gidsWithInBundleShipping:", Array.from(gidsWithInBundleShipping));
    console.log("bundledGids:", Array.from(bundledGids));
    console.log("standaloneShippingLines:", standaloneShippingLines);
    console.log("proposedOrphanShippingUpdates (qty=0):", orphanUpdates);
    console.log(
      "orphan update keys count:",
      Object.keys(orphanUpdates).length
    );
    console.groupEnd();

    console.group("4. Raw cart payload (cart.js)");
    console.log(cloneForDebugLog(cart));
    console.groupEnd();

    console.warn(
      "[ExpressCheckout DEBUG] Redirect is disabled. Set EXPRESS_CHECKOUT_DEBUG_DISABLE_REDIRECT to false in express-checkout.js to restore."
    );
    console.groupEnd();

    return cart;
  }

  function redirectToCheckout() {
    const checkoutUrl = rootUrl().replace(/\/?$/, "") + "/checkout";
    markReturnRefreshPending();
    resetExpressCheckoutUI();
    // Allow one paint frame so the loader is hidden before navigation (and in bfcache snapshot).
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.location.href = checkoutUrl;
      });
    });
  }

  async function runExpressCheckout(button) {
    if (inProgress || isFreeGiftCartBusy()) return;
    inProgress = true;
    setButtonLoading(button, true);

    try {
      window.__expressCheckoutCartBusy = true;
      // processBundles() refreshes the component map itself right after this read.
      const cart = await fetchCart();
      if (!cart.items || !cart.items.length) {
        throw new Error("Your cart is empty");
      }

      await processBundles(cart);
      await setExpressCheckoutAttribute();

      if (EXPRESS_CHECKOUT_DEBUG_DISABLE_REDIRECT) {
        await logExpressCheckoutDebugBeforeRedirect();
        resetExpressCheckoutUI();
        inProgress = false;
        return;
      }

      redirectToCheckout();
    } catch (err) {
      console.error("[ExpressCheckout]", err);
      resetExpressCheckoutUI();
      window.alert(
        err && err.message
          ? err.message
          : "Express checkout could not be completed. Please try again."
      );
    } finally {
      window.__expressCheckoutCartBusy = false;
    }
  }

  function onDocumentClick(event) {
    const button = event.target.closest("#express-checkout, [data-express-checkout]");
    if (!button) return;
    event.preventDefault();
    if (isFreeGiftCartBusy()) return;
    if (button.getAttribute("aria-disabled") === "true" || button.hasAttribute("disabled")) {
      return;
    }
    runExpressCheckout(button);
  }

  function handlePageShow(event) {
    if (shouldRefreshAfterExpressCheckout(event)) {
      setTimeout(function () {
        window.location.reload();
      }, 150);
      return;
    }
    resetExpressCheckoutUI();
    window.__expressCheckoutCartBusy = false;
  }

  function initExpressCheckout() {
    window.chukarRefreshCartItemComponents = refreshCartItemComponents;
    window.chukarRefreshCartDrawerSection = refreshCartDrawerSection;
    window.chukarClearExpressCheckoutCartAttribute =
      clearExpressCheckoutCartAttributeIfActive;
    window.chukarForceExpressCheckoutCartAttributeFalse =
      clearExpressCheckoutCartAttribute;
    document.addEventListener("click", onDocumentClick);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("pagehide", function () {
      resetExpressCheckoutUI();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initExpressCheckout);
  } else {
    initExpressCheckout();
  }
})();
